import React, { useState } from "react";
import {
    BadgePercent,
    Building2,
    Calendar,
    Camera,
    ChevronDown,
    ChevronUp,
    Coins,
    ExternalLink,
    FileText,
    FolderOpen,
    Globe,
    IdCard,
    Mail,
    Pencil,
    Phone,
    ScrollText,
    Trash
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CustomerStatus, PaymentMethodType, WorkspaceType } from "shared-types";
import { Button } from "./Button";
import {
    LeadInteractionDetails
} from "../../../MainMenu/LeadAndCustomer/Components/Interactions/leadInteractionDetails";

export interface CustomerCardProps {
    id: string;
    name: string;
    phone: string;
    email: string;
    businessName: string;
    businessType: string;
    status: CustomerStatus;
    image?: string;
    idNumber?: string;
    currentWorkspaceType?: WorkspaceType;
    workspaceCount?: number;
    contractSignDate?: string;
    contractStartDate?: string;
    billingStartDate?: string;
    notes?: string;
    invoiceName?: string;
    contractDocumentUrl?: string;
    paymentMethodType?: PaymentMethodType;
    ip: string;
    createdAt?: string;
    updatedAt?: string;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}

const statusColors: Record<CustomerStatus, string> = {
    ACTIVE: "bg-green-100 text-green-800",
    NOTICE_GIVEN: "bg-yellow-100 text-yellow-800",
    EXITED: "bg-red-100 text-red-800",
    PENDING: "bg-gray-100 text-gray-800",
    CREATED: "bg-blue-100 text-blue-800",
};

const statusLabels: Record<CustomerStatus, string> = {
    ACTIVE: "פעיל",
    NOTICE_GIVEN: "הודעת עזיבה",
    EXITED: "עזב",
    PENDING: "בהמתנה",
    CREATED: "נוצר"
};

const workspaceTypeLabels: Record<WorkspaceType, string> = {
    PRIVATE_ROOM1: "חדר פרטי",
    PRIVATE_ROOM2: "חדר של 2",
    PRIVATE_ROOM3: "חדר של 3",
    DESK_IN_ROOM: "שולחן בחדר",
    OPEN_SPACE: "אופן ספייס",
    KLIKAH_CARD: "כרטיס קליקה",
    KLIKAH_CARD_UPGRADED: "כרטיס קליקה משודרג",
    DOOR_PASS: "דלת כניסה",
    WALL: "קיר",
    COMPUTER_STAND: "עמדת מחשב",
    RECEPTION_DESK: "דלפק קבלה",
    BASE: "בסיס"

};

const paymentMethodLabels: Record<PaymentMethodType, string> = {
    CREDIT_CARD: "כרטיס אשראי",
    BANK_TRANSFER: "העברה בנקאית",
    CHECK: "שיק",
    CASH: "מזומן",
    OTHER: "אחר",
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "לא זמין";
    const date = new Date(dateString);
    return `${ date.getDate().toString().padStart(2, "0") }/${ (date.getMonth() + 1)
        .toString()
        .padStart(2, "0") }/${ date.getFullYear().toString().slice(-2) }`;
};

const getColorForChar = (char: string) => {
    const colors = [
        "bg-red-200 text-red-800",
        "bg-green-200 text-green-800",
        "bg-blue-200 text-blue-800",
        "bg-yellow-200 text-yellow-800",
        "bg-purple-200 text-purple-800",
        "bg-pink-200 text-pink-800",
        "bg-indigo-200 text-indigo-800",
        "bg-teal-200 text-teal-800",
    ];
    const index = char.toUpperCase().charCodeAt(0) % colors.length;
    return colors[index];
};

export const ExpandableCustomerCard = ({
                                           id,
                                           name,
                                           phone,
                                           email,
                                           businessName,
                                           businessType,
                                           status,
                                           image,
                                           idNumber,
                                           currentWorkspaceType,
                                           workspaceCount,
                                           contractSignDate,
                                           contractStartDate,
                                           billingStartDate,
                                           notes,
                                           invoiceName,
                                           contractDocumentUrl,
                                           paymentMethodType,
                                           ip,
                                           createdAt,
                                           updatedAt,
                                           onEdit,
                                           onDelete,
                                       }: CustomerCardProps) => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const handleEditImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`updateImage/${ id }`);
    };

    const initials = name?.charAt(0).toUpperCase();
    const avatarColor = getColorForChar(initials || "A");
    const hasImage = !!image;

    return (
        <div className="rounded-xl border shadow p-2 mb-0.5 bg-white transition-all duration-300 text-right">
            <div className="cursor-pointer" onClick={ () => setOpen(!open) }>
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            { hasImage ? (
                                <img
                                    src={ image }
                                    alt={ name }
                                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-300 hover:border-blue-500 cursor-pointer"
                                    onClick={ handleEditImage }
                                />
                            ) : (
                                <div
                                    className={ `w-10 h-10 flex items-center justify-center rounded-full font-bold cursor-pointer ${ avatarColor }` }
                                    onClick={ handleEditImage }
                                >
                                    { initials }
                                </div>
                            ) }
                            <Camera
                                size={ 14 }
                                className="absolute -bottom-1 -left-1 bg-white rounded-full p-[1px] border cursor-pointer"
                                onClick={ handleEditImage }
                            />
                        </div>

                        <div className="text-xl font-bold text-gray-900">{ name }</div>

                        <div
                            className={ `text-xs px-3 py-1 rounded-xl font-semibold flex items-center gap-2 ${ statusColors[status] }` }
                            onClick={ (e) => {
                                e.stopPropagation();
                                navigate(`updateStatus/${ id }`);
                            } }
                        >
                            { statusLabels[status] }
                            <Pencil size={ 12 }/>
                        </div>
                    </div>
                    { open ? (
                        <ChevronUp className="text-gray-600"/>
                    ) : (
                        <ChevronDown className="text-gray-600"/>
                    ) }
                </div>

                <div className="mt-4 flex justify-between flex-wrap gap-y-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <Phone size={ 16 }/> { phone }
                    </div>
                    <div className="flex items-center gap-2">
                        <Mail size={ 16 }/> { email }
                    </div>
                    <div className="flex items-center gap-2">
                        <Building2 size={ 16 }/> { businessName }
                    </div>
                </div>
            </div>

            { open && (
                <div className="mt-6 border-t pt-5 text-sm bg-slate-700 p-6 rounded-lg">
                    {/* Two column layout with equal heights */ }
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left column - Customer Details */ }
                        <div className="space-y-4">
                            <div className="h-32">
                                <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">פרטי
                                    הלקוח</h4>
                                <div className="h-24 space-y-2 bg-white p-4 rounded-lg shadow-md">
                                    <div className="flex items-center gap-2">
                                        <IdCard size={ 14 } className="text-blue-600"/> <span
                                        className="font-medium text-gray-700">ת"ז:</span> <span
                                        className="text-gray-900">{ idNumber || "לא זמין" }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <BadgePercent size={ 14 } className="text-blue-600"/> <span
                                        className="font-medium text-gray-700">תחום עיסוק:</span> <span
                                        className="text-gray-900">{ businessType }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Globe size={ 14 } className="text-blue-600"/> <span
                                        className="font-medium text-gray-700">כתובת IP:</span> <span
                                        className="text-gray-900">{ ip || "לא זמין" }</span>
                                    </div>
                                </div>
                            </div>

                            {/* Prominent seating information */ }
                            <div className="h-32">
                                <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">מקום
                                    הישיבה</h4>
                                <div className="h-24 bg-white p-4 rounded-lg shadow-md">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-4 h-4 bg-blue-600 rounded-full flex-shrink-0"></div>
                                        <div>
                                            <div className="font-semibold text-blue-800 text-sm">
                                                { workspaceTypeLabels[currentWorkspaceType!] || "לא נקבע מקום" }
                                            </div>
                                            <div className="text-sm text-blue-600">
                                                מקומות עבודה: { workspaceCount ?? "לא זמין" }
                                            </div>
                                        </div>
                                    </div>
                                    { currentWorkspaceType && (
                                        <div
                                            className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded inline-block">
                                            קוד: { currentWorkspaceType }
                                        </div>
                                    ) }
                                </div>
                            </div>

                            <div className="h-40">
                                <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">תאריכים</h4>
                                <div className="bg-white p-4 rounded-lg shadow-md">
                                    <div className="flex items-center gap-2">
                                        <ScrollText size={ 14 } className="text-green-600"/> <span
                                        className="font-medium text-gray-700">חתימה:</span> <span
                                        className="text-gray-900">{ formatDate(contractSignDate) }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={ 14 } className="text-green-600"/> <span
                                        className="font-medium text-gray-700">התחלת חוזה:</span> <span
                                        className="text-gray-900">{ formatDate(contractStartDate) }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={ 14 } className="text-green-600"/> <span
                                        className="font-medium text-gray-700">תחילת חיוב:</span> <span
                                        className="text-gray-900">{ formatDate(billingStartDate) }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={ 14 } className="text-green-600"/> <span
                                        className="font-medium text-gray-700">נוצר:</span> <span
                                        className="text-gray-900">{ formatDate(createdAt) }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={ 14 } className="text-green-600"/> <span
                                        className="font-medium text-gray-700">עודכן:</span> <span
                                        className="text-gray-900">{ formatDate(updatedAt) }</span>
                                    </div>
                                </div>
                            </div>

                            <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">הערות</h4>
                            <div className="h-16 bg-white p-4 rounded-lg shadow-md mb-4">
                                <div className="flex items-start gap-2">
                                    <FileText size={ 14 } className="mt-0.5 flex-shrink-0 text-pink-600"/>
                                    <span className="text-gray-900">{ notes || "אין הערות" }</span>
                                </div>
                            </div>
                        </div>

                        {/* Right column - Payment, Documents & Interactions */ }
                        <div className="space-y-4">
                            <div className="h-32">
                                <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">תשלום
                                    וחיוב</h4>
                                <div className="h-24 space-y-2 bg-white p-4 rounded-lg shadow-md">
                                    <div className="flex items-center gap-2">
                                        <Coins size={ 14 } className="text-yellow-600"/> <span
                                        className="font-medium text-gray-700">אמצעי תשלום:</span> <span
                                        className="text-gray-900">{ paymentMethodLabels[paymentMethodType!] || "לא זמין" }</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FileText size={ 14 } className="text-yellow-600"/> <span
                                        className="font-medium text-gray-700">חשבונית ע"ש:</span> <span
                                        className="text-gray-900">{ invoiceName || "לא זמין" }</span>
                                    </div>
                                </div>
                            </div>

                            <div className="h-32">
                                <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">מסמכים</h4>
                                <div className="h-24 bg-white p-3 rounded-lg shadow-md flex flex-col justify-center">
                                    <div className="flex gap-2 justify-center items-center">
                                        <button
                                            onClick={ () => {
                                                const driveUrl = `https://drive.google.com/drive/search?q=${ encodeURIComponent(email) }`;
                                                window.open(driveUrl, '_blank');
                                            } }
                                            className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                        >
                                            <FolderOpen size={ 14 }/>
                                            <span>תיקיית Google Drive</span>
                                        </button>
                                        { contractDocumentUrl && (
                                            <button
                                                onClick={ () => {
                                                    window.open(contractDocumentUrl, '_blank');
                                                } }
                                                className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors"
                                            >
                                                <ExternalLink size={ 14 }/>
                                                <span>צפה בחוזה</span>
                                            </button>
                                        ) }
                                    </div>
                                </div>
                            </div>


                            <h4 className="font-semibold text-white mb-3 text-base border-b border-slate-500 pb-1">אינטראקציות</h4>
                            <div className="h-60 bg-white p-4 rounded-lg shadow-md">
                                <LeadInteractionDetails hideHeader={ true }/>
                            </div>
                        </div>
                    </div>

                    <div
                        className="flex flex-wrap justify-between items-center mt-6 pt-4 border-t border-slate-500 gap-2">
                        <div className="flex gap-3 text-blue-300 font-medium text-sm">
                        </div>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                color="gray"
                                onClick={ () => onEdit(id) }
                                className="flex gap-1 items-center"
                            >
                                <Pencil size={ 14 }/> עריכה
                            </Button>
                            <Button
                                size="sm"
                                color="red"
                                onClick={ () => onDelete(id) }
                                className="flex gap-1 items-center"
                            >
                                <Trash size={ 14 }/> מחיקה
                            </Button>
                        </div>
                    </div>
                </div>
            ) }
        </div>
    );
};